/* eslint-disable n/no-unsupported-features/node-builtins -- this is a browser environment */

const minimumIosVersion = '13.0'
const minimumAndroidVersion = '6.0'

export type AppUpdateType = 'unsupported' | 'major' | 'minor' | null

type SemanticVersion = {
  major: number
  minor: number
  patch: number
  isBelow: (that: SemanticVersion) => boolean
}

type AppVersion = {
  current: string
  advised: string
  updateType: AppUpdateType
}

const parseVersion = (version: string): SemanticVersion => {
  const [major = 0, minor = 0, patch = 0] = version.split('.').map((n: string) => {
    try {
      return parseInt(n, 10)
    } catch {
      return 0
    }
  })

  const isBelow = (that: SemanticVersion): boolean =>
    that.major > major ||
    (that.major === major && that.minor > minor) ||
    (that.major === major && that.minor === minor && that.patch > patch)

  return {
    major: major === 1 ? 4 : major, // We straightened out the version number (1.6.6 to 4.6.0) for iOS in February 2022. This is should be close enough a we require 4.6.0
    minor,
    patch,
    isBelow,
  }
}

const getCurrentNativeAppVersion = (userAgent: string): string => {
  const pattern = /DHL_WEBVIEW\(.*?\/(\d+\.\d+\.\d+)/
  return userAgent.match(pattern)?.[1] ?? '0.0.0'
}

export const getCurrentNativeAppVersionFromUserAgent = (): string =>
  getCurrentNativeAppVersion(navigator?.userAgent ?? '')

const determineIsUnsupported = (userAgent: string): boolean => {
  const currentIosVersion = userAgent
    .match(/(iPhone|iPad) OS.*like Mac OS X/)?.[0]
    ?.replace(/[^\d._]/g, '')
    .replace(/_/g, '.')

  if (currentIosVersion) {
    return parseVersion(currentIosVersion).isBelow(parseVersion(minimumIosVersion))
  }

  const currentAndroidVersion = userAgent.match(/Android\s([\d\\.]+)/)?.[1]
  if (currentAndroidVersion) {
    return parseVersion(currentAndroidVersion).isBelow(parseVersion(minimumAndroidVersion))
  }

  return false
}

const updateType = (
  currentVersion: string,
  advisedVersion: string,
  lastSupportedVersion: string,
  isUnsupported: boolean
): AppUpdateType => {
  if (isUnsupported) {
    return 'unsupported'
  }

  const current = parseVersion(currentVersion)
  const advised = parseVersion(advisedVersion)
  const lastSupported = parseVersion(lastSupportedVersion)

  if (current.isBelow(lastSupported)) {
    return 'major'
  }

  // We do not want to force a mandatory update until we decide there is a breaking change.
  if (current.isBelow(advised)) {
    return 'minor'
  }

  return null
}

export const isFeatureEnabledWithVersion = (minVersion: string, currentVersion: string): boolean =>
  parseVersion(minVersion).isBelow(parseVersion(currentVersion)) || minVersion === currentVersion

type Props = {
  advisedAppVersion: string
  mandatoryAppVersion: string
}

export const useAppVersion = ({ advisedAppVersion, mandatoryAppVersion }: Props): AppVersion => {
  const currentVersion = getCurrentNativeAppVersionFromUserAgent()
  const update = updateType(
    currentVersion,
    advisedAppVersion,
    mandatoryAppVersion,
    determineIsUnsupported(navigator?.userAgent ?? '')
  )
  return {
    current: currentVersion,
    advised: advisedAppVersion,
    updateType: update,
  }
}
